import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { isError } from '@app/functions/is-error';
import { ProductSubtypeEnum } from '@app/models/enums/product-subtype.enum';
import { ProductTypeEnum } from '@app/models/enums/product-type.enum';
import { FilterSearchRequestInterface, FilterSearchResponseInterface } from '@app/models/requests/filter-search-request.interface';
import { TaqmanProductCountRequestInterface, TaqmanSearchRequestInterface } from '@app/models/requests/taqman-search-request.interface';
import { TaqmanGlobalSearchResponseInterface, TaqmanSearchResponseInterface } from '@app/models/responses/taqman-search-response.interface';
import { TfErrorModel } from '@app/models/tf-error.model';
import { catchError, map, Observable, throwError } from 'rxjs';
import { ProductSiteRegionService } from './product-site-region.service';

@Injectable({
    providedIn: 'root'
})
export class TfApiService {

    constructor(
        private httpClient: HttpClient,
        private readonly productSiteRegionService: ProductSiteRegionService,

    ) {
    }

    search(params: TaqmanSearchRequestInterface): Observable<TaqmanSearchResponseInterface> {

        const newParams = {
            ...params,
            productType: this.getProductTypeAlias(params.productType),
            productSubtype: this.getProductSubtypeAlias(params.productSubtype)
        };

        if (params.productSubtype === ProductSubtypeEnum.microrna_array) {
            newParams.productType = 'mirna_arrays';
        }

        return this.httpClient.post<TaqmanSearchResponseInterface>('/api/magellan/product/taqmanSearch', newParams)
            .pipe(
                map((response) => {

                    if (isError(response)) {
                        throw new TfErrorModel('DATA_ERROR', response.message);
                    }

                    return response;

                }),
                catchError(err => {
                    return throwError(() => new TfErrorModel('HTTP_ERROR', err.description));
                })
            );

    }

    searchProductCounts(params: TaqmanProductCountRequestInterface): Observable<TaqmanGlobalSearchResponseInterface> {

        const url: string = '/api/magellan/product/productSearchCount';
        if(params.keyword === null || params.keyword === undefined || params.keyword === ''){
            params.keyword = '*';
        }

        if (this.productSiteRegionService.isFisherSci() === true) {
            params.isFSCI = true;
        }

        const payload = {
            ...params,
            isBroadSearch: true
        }

        return this.httpClient.post<TaqmanGlobalSearchResponseInterface>(url, payload)
            .pipe(
                map((response) => {
                    if (isError(response)) {
                        throw new TfErrorModel('DATA_ERROR', response.message);
                    }
                    return response;
                }),
                catchError(err => {
                    return throwError(() => new TfErrorModel('HTTP_ERROR', err.description));
                })
            );
    }

    filterSearch(params: FilterSearchRequestInterface): Observable<FilterSearchResponseInterface[]> {
        return this.httpClient.post<FilterSearchResponseInterface[]>(`/api/magellan/product/getFilterSuggestions`, params)
            .pipe(
                map((response) => {
                    if (isError(response)) {
                        throw response;
                    }
                    return response;
                }),
                catchError(err => {
                    return throwError(err);
                })
            );
    }

    private getProductTypeAlias(productType: ProductTypeEnum): string {

        switch (productType) {
            case ProductTypeEnum.microrna:
                return 'mirna'
        }

        return productType;

    }

    /**
     * Some product subtypes have different aliases in the backend
     * @param productSubType
     * @private
     */
    private getProductSubtypeAlias(productSubType: ProductSubtypeEnum): string {

        switch (productSubType) {

            case ProductSubtypeEnum.microrna_advanced:
                return 'advanced_mirna';

            case ProductSubtypeEnum.microrna_classic:
                return 'classic_mirna';

            case ProductSubtypeEnum.microrna_primary:
                return 'pri_mirna';

            case ProductSubtypeEnum.microrna_array:
                return 'mirna_arrays';

        }

        return productSubType;

    }

}
